import Editor, { OnMount } from '@monaco-editor/react'
import CloseIcon from '@mui/icons-material/Close'
import FullscreenIcon from '@mui/icons-material/Fullscreen'
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit'
import SaveIcon from '@mui/icons-material/Save'
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	IconButton,
	Tooltip,
	Typography,
} from '@mui/material'
import { useCallback, useEffect, useRef, useState } from 'react'

import { EditorModalHeader } from '@/components/EditorModalHeader'
import { useAppContext } from '@/hooks'
import { APP_FONT_FAMILY } from '@/styles/theme'
import { registerSqlAutocomplete } from '@/utils/monaco/sqlAutocomplete'

export type EditorLanguage = 'sql' | 'markdown' | 'text' | 'javascript' | 'json'

interface FullscreenEditorModalProps {
	/** Element/row identity shown next to the attribute in the header */
	elementLabel?: string,
	/** When true, enable SQL script autocomplete (keywords, functions, snippets). Defaults to language === 'sql'. */
	isSqlScript?: boolean,
	language?: EditorLanguage,
	onClose: () => void,
	onSave: (value: string) => void,
	open: boolean,
	readOnly?: boolean,
	title?: string,
	value: string
}

/**
 * Fullscreen Monaco Editor Modal.
 * Shortcuts (work but not shown in UI per MDD-2602): F11 toggle fullscreen, Ctrl+S save, Esc close.
 */
export const FullscreenEditorModal = ({
	open,
	onClose,
	onSave,
	value,
	title: titleProp,
	elementLabel,
	language = 'sql',
	isSqlScript,
	readOnly = false,
}: FullscreenEditorModalProps) => {
	const enableSqlAutocomplete = isSqlScript ?? language === 'sql'
	const { t } = useAppContext()
	const title = titleProp ?? t('SCRIPT_EDITOR')
	const [editorValue, setEditorValue] = useState(value)
	const [isFullscreen, setIsFullscreen] = useState(true)
	const [showUnsavedConfirm, setShowUnsavedConfirm] = useState(false)
	// Monaco editor instance; typed as any to avoid pulling in full monaco-editor typings
	const editorRef = useRef<any>(null)
	const editorValueRef = useRef(editorValue)
	const initialValueRef = useRef(value)
	const prevOpenRef = useRef(false)

	// Keep ref in sync with state
	useEffect(() => {
		editorValueRef.current = editorValue
	}, [editorValue])

	// When modal opens: sync editor to value and capture initial for dirty check.
	// When modal is closed: sync so next open has latest value. Don't overwrite while open.
	useEffect(() => {
		if (open) {
			if (!prevOpenRef.current) {
				initialValueRef.current = value
				setEditorValue(value)
			}
		} else {
			setEditorValue(value)
		}
		prevOpenRef.current = open
	}, [open, value])

	// Handle save - uses ref to avoid stale closure
	const handleSave = useCallback(() => {
		if (!readOnly) {
			onSave(editorValueRef.current)
		}
		setShowUnsavedConfirm(false)
		onClose()
	}, [onSave, onClose, readOnly])

	// Handle editor mount
	const handleEditorMount: OnMount = (editor, monaco) => {
		editorRef.current = editor

		if (enableSqlAutocomplete) {
			registerSqlAutocomplete(monaco)
		}

		// Add Ctrl+S / Cmd+S keybinding
		editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => {
			if (!readOnly) {
				onSave(editorValueRef.current)
			}
			setShowUnsavedConfirm(false)
			onClose()
		})

		// Focus the editor
		editor.focus()
	}

	// Request close: if dirty, show confirm; else close
	const requestClose = useCallback(() => {
		if (!readOnly && editorValueRef.current !== initialValueRef.current) {
			setShowUnsavedConfirm(true)
		} else {
			onClose()
		}
	}, [onClose, readOnly])

	// Close without saving (from confirm dialog)
	const handleDontSave = useCallback(() => {
		setShowUnsavedConfirm(false)
		onClose()
	}, [onClose])

	// Handle close without saving (direct close when not dirty)
	const handleClose = useCallback(() => {
		requestClose()
	}, [requestClose])

	// Toggle fullscreen
	const toggleFullscreen = useCallback(() => {
		setIsFullscreen((prev) => !prev)
	}, [])

	// Handle keyboard shortcuts at dialog level
	useEffect(() => {
		if (!open) return

		const handleKeyDown = (e: KeyboardEvent) => {
			// F11: Toggle fullscreen
			if (e.key === 'F11') {
				e.preventDefault()
				toggleFullscreen()
			}

			// Escape: Close (let the dialog handle this naturally)
		}

		window.addEventListener('keydown', handleKeyDown)
		return () => window.removeEventListener('keydown', handleKeyDown)
	}, [open, toggleFullscreen])

	// Get Monaco language ID
	const getMonacoLanguage = (lang: EditorLanguage): string => {
		switch (lang) {
			case 'sql':
				return 'sql'
			case 'markdown':
				return 'markdown'
			case 'javascript':
				return 'javascript'
			case 'json':
				return 'json'
			default:
				return 'plaintext'
		}
	}

	return (
		<>
		<Dialog
			open={open}
			onClose={handleClose}
			fullScreen={isFullscreen}
			maxWidth={isFullscreen ? false : 'lg'}
			fullWidth
			sx={{ zIndex: 1400 }}
			PaperProps={{
				sx: {
					height: isFullscreen ? '100vh' : '80vh',
					maxHeight: isFullscreen ? '100vh' : '80vh',
				},
			}}
		>
			<DialogTitle
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					py: 1,
					px: 2,
					borderBottom: '1px solid',
					borderColor: 'divider',
				}}
			>
				<Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
					<Typography variant="h6" component="span">
						<EditorModalHeader
							attributeLabel={title}
							elementLabel={elementLabel}
						/>
					</Typography>
				</Box>
				<Box sx={{ display: 'flex', gap: 1 }}>
					<Tooltip title={t('TOGGLE_FULLSCREEN')}>
						<IconButton onClick={toggleFullscreen} size="small" aria-label={t('TOGGLE_FULLSCREEN')}>
							{isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
						</IconButton>
					</Tooltip>
					<Tooltip title={t('CLOSE')}>
						<IconButton onClick={handleClose} size="small" aria-label={t('CLOSE')}>
							<CloseIcon />
						</IconButton>
					</Tooltip>
				</Box>
			</DialogTitle>

			<DialogContent sx={{ p: 0, overflow: 'hidden' }}>
				<Editor
					height="100%"
					language={getMonacoLanguage(language)}
					value={editorValue}
					onChange={(value) => setEditorValue(value ?? '')}
					onMount={handleEditorMount}
					options={{
						readOnly,
						minimap: { enabled: true, showSlider: 'always' },
						fontSize: 14,
						fontFamily: APP_FONT_FAMILY,
						lineNumbers: enableSqlAutocomplete ? 'on' : 'off',
						wordWrap: 'on',
						automaticLayout: true,
						scrollBeyondLastLine: false,
						tabSize: 2,
						theme: 'vs-light',
						renderLineHighlight: 'none',
						// Autocomplete widget settings
						suggestFontSize: 13,
						suggestLineHeight: 20,
						suggest: {
							showStatusBar: false,
							showIcons: true,
							maxVisibleSuggestions: 10,
						},
						fixedOverflowWidgets: true,
					}}
				/>
			</DialogContent>

			<DialogActions
				sx={{
					px: 2,
					py: 1,
					borderTop: '1px solid',
					borderColor: 'divider',
				}}
			>
				{readOnly && (
					<Typography variant="caption" color="text.secondary" sx={{ mr: 'auto' }}>
						{t('READ_ONLY_MODE')}
					</Typography>
				)}
				<Box sx={{ ml: readOnly ? 0 : 'auto', display: 'flex', gap: 1 }}>
					<Button onClick={handleClose} color="inherit">
						{t('CANCEL')}
					</Button>
					{!readOnly && (
						<Button
							onClick={handleSave}
							variant="contained"
							color="primary"
							startIcon={<SaveIcon />}
						>
							{t('SAVE')}
						</Button>
					)}
				</Box>
			</DialogActions>
		</Dialog>

		{/* MDD-2602: Confirm when closing with unsaved changes (zIndex above editor modal) */}
		<Dialog
			open={showUnsavedConfirm}
			onClose={() => setShowUnsavedConfirm(false)}
			aria-labelledby="unsaved-changes-dialog-title"
			sx={{ zIndex: 1500 }}
			slotProps={{ backdrop: { sx: { zIndex: 1500 } } }}
			PaperProps={{ sx: { zIndex: 1500 } }}
		>
			<DialogTitle id="unsaved-changes-dialog-title">
				{t('UNSAVED_CHANGES_TITLE')}
			</DialogTitle>
			<DialogContent>
				<DialogContentText>
					{t('UNSAVED_CHANGES_SAVE_PROMPT')}
				</DialogContentText>
			</DialogContent>
			<DialogActions>
				<Button onClick={() => setShowUnsavedConfirm(false)} color="inherit">
					{t('CANCEL')}
				</Button>
				<Button onClick={handleDontSave} color="inherit">
					{t('DONT_SAVE')}
				</Button>
				<Button onClick={handleSave} variant="contained" color="primary" startIcon={<SaveIcon />}>
					{t('SAVE')}
				</Button>
			</DialogActions>
		</Dialog>
		</>
	)
}

